import { useState, useEffect, useCallback } from 'react'
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Checkbox, FormControl, InputLabel, Select, MenuItem,
  Pagination, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Alert, Snackbar, Tooltip,
} from '@mui/material'
import {
  CheckCircle, Edit, Visibility, VisibilityOff, Close,
} from '@mui/icons-material'
import { kbApi } from '../services/api'

const SEVERITIES = ['critical', 'high', 'medium', 'low']
const DOMAINS = ['framework_patterns', 'security_vulnerabilities', 'best_practices', 'regulatory_compliance', 'scoring_rubrics']
const PAGE_SIZE = 20

const getSeverityColor = (s: string) => {
  const map: any = { critical: 'error', high: 'warning', medium: 'info', low: 'success' }
  return map[s] || 'default'
}

const KBArticleReviewPanel = () => {
  const [rules, setRules] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [domainFilter, setDomainFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [validationFilter, setValidationFilter] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expandedRule, setExpandedRule] = useState<string | null>(null)
  const [editRule, setEditRule] = useState<any>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })
  const [stats, setStats] = useState<any>(null)
  const [saving, setSaving] = useState(false)

  const fetchRules = useCallback(async (p = page) => {
    setLoading(true)
    try {
      const params: any = { limit: PAGE_SIZE, offset: (p - 1) * PAGE_SIZE, active_only: false }
      if (domainFilter) params.domain = domainFilter
      if (severityFilter) params.severity = severityFilter
      if (validationFilter) params.validated_by = validationFilter
      const res = await kbApi.getRules(params)
      const data = res.data
      setRules(data.rules || data || [])
      setTotal(data.total || 0)
    } catch (e) {
      console.error('Failed to fetch rules:', e)
    } finally {
      setLoading(false)
    }
  }, [page, domainFilter, severityFilter, validationFilter])

  useEffect(() => {
    fetchRules(1)
    setPage(1)
    setSelected(new Set())
  }, [domainFilter, severityFilter, validationFilter]) // eslint-disable-line

  useEffect(() => {
    kbApi.getStats().then(r => setStats(r.data)).catch(() => {})
  }, [])

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage)
    setSelected(new Set())
    fetchRules(newPage)
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === rules.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(rules.map(r => r.id)))
    }
  }

  const handleBatchValidate = async () => {
    try {
      await kbApi.batchValidate(Array.from(selected))
      setSnackbar({ open: true, message: `${selected.size} articles validated`, severity: 'success' })
      setSelected(new Set())
      fetchRules()
    } catch (e) {
      setSnackbar({ open: true, message: 'Batch validation failed', severity: 'error' })
    }
  }

  const handleValidateSingle = async (id: string) => {
    try {
      await kbApi.validateRule(id)
      setSnackbar({ open: true, message: 'Article validated', severity: 'success' })
      fetchRules()
    } catch (e) {
      setSnackbar({ open: true, message: 'Validation failed', severity: 'error' })
    }
  }

  const handleEditSave = async () => {
    if (!editRule) return
    setSaving(true)
    try {
      await kbApi.updateRule(editRule.id, editRule)
      setSnackbar({ open: true, message: 'Article updated', severity: 'success' })
      setEditRule(null)
      fetchRules()
    } catch (e) {
      setSnackbar({ open: true, message: 'Update failed', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const openEdit = async (ruleId: string) => {
    try {
      const res = await kbApi.getRule(ruleId)
      setEditRule(res.data)
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load rule', severity: 'error' })
    }
  }

  const autoIngestedCount = stats ? (stats.total_rules || stats.total || 0) : total
  const validatedCount = stats?.validated_count || 0

  return (
    <Box>
      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Articles</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{autoIngestedCount - validatedCount}</Typography>
            <Typography variant="body2" color="text.secondary">Need Review</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{validatedCount}</Typography>
            <Typography variant="body2" color="text.secondary">Validated</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">{stats?.frameworks_count || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Frameworks</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Validation Status</InputLabel>
                <Select value={validationFilter} label="Validation Status" onChange={e => setValidationFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="auto_ingested">Needs Review</MenuItem>
                  <MenuItem value="human_expert">Validated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Domain</InputLabel>
                <Select value={domainFilter} label="Domain" onChange={e => setDomainFilter(e.target.value)}>
                  <MenuItem value="">All Domains</MenuItem>
                  {DOMAINS.map(d => <MenuItem key={d} value={d}>{d.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select value={severityFilter} label="Severity" onChange={e => setSeverityFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              {selected.size > 0 && (
                <Button
                  variant="contained" color="success" size="small"
                  startIcon={<CheckCircle />}
                  onClick={handleBatchValidate}
                >
                  Validate {selected.size} Selected
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={rules.length > 0 && selected.size === rules.length}
                        indeterminate={selected.size > 0 && selected.size < rules.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule: any) => {
                    const meta = rule.metadata_ext || {}
                    const isValidated = meta.validated_by === 'human_expert'
                    const isExpanded = expandedRule === rule.id
                    return (
                      <>
                        <TableRow key={rule.id} hover sx={{ cursor: 'pointer' }}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected.has(rule.id)} onChange={() => toggleSelect(rule.id)} />
                          </TableCell>
                          <TableCell onClick={() => setExpandedRule(isExpanded ? null : rule.id)}>
                            <Typography variant="subtitle2">{rule.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{rule.framework || 'general'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={(rule.domain || '').replace(/_/g, ' ')} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={rule.severity || 'medium'} color={getSeverityColor(rule.severity) as any} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isValidated ? 'Validated' : 'Needs Review'}
                              color={isValidated ? 'success' : 'default'}
                              size="small"
                              variant={isValidated ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(rule.id)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                            {!isValidated && (
                              <Tooltip title="Validate">
                                <IconButton size="small" color="success" onClick={() => handleValidateSingle(rule.id)}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${rule.id}-detail`}>
                            <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', py: 2, px: 3 }}>
                              <Typography variant="subtitle2" gutterBottom>Description</Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>{rule.description || 'No description'}</Typography>
                              {rule.remediation && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>Remediation</Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>{rule.remediation}</Typography>
                                </>
                              )}
                              {rule.pattern && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>Pattern</Typography>
                                  <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {rule.pattern}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )
                  })}
                  {rules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No articles match the current filters</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={Math.ceil(total / PAGE_SIZE)}
                page={page}
                onChange={handlePageChange}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editRule} onClose={() => setEditRule(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit KB Article
          <IconButton onClick={() => setEditRule(null)}><Close /></IconButton>
        </DialogTitle>
        {editRule && (
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Title" value={editRule.name || ''}
                  onChange={e => setEditRule({ ...editRule, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Description" multiline rows={4} value={editRule.description || ''}
                  onChange={e => setEditRule({ ...editRule, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select value={editRule.severity || 'medium'} label="Severity" onChange={e => setEditRule({ ...editRule, severity: e.target.value })}>
                    {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Domain</InputLabel>
                  <Select value={editRule.domain || ''} label="Domain" onChange={e => setEditRule({ ...editRule, domain: e.target.value })}>
                    {DOMAINS.map(d => <MenuItem key={d} value={d}>{d.replace(/_/g, ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Remediation" multiline rows={3} value={editRule.remediation || ''}
                  onChange={e => setEditRule({ ...editRule, remediation: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Pattern (regex)" value={editRule.pattern || ''}
                  onChange={e => setEditRule({ ...editRule, pattern: e.target.value })}
                  InputProps={{ sx: { fontFamily: 'monospace' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth label="Framework" value={editRule.framework || ''}
                  onChange={e => setEditRule({ ...editRule, framework: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth label="Category" value={editRule.category || ''}
                  onChange={e => setEditRule({ ...editRule, category: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditRule(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save & Validate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default KBArticleReviewPanel
